import {
	HubItemCategoryNodeKind
} from "client/Api/Hub/common";
import {
	FrequentlyAskedQuestionElementSaveModel,
	FrequentlyAskedQuestionItemSaveModel,
	HubItemColumnSaveModel,
	HubItemElementSaveModel,
	HubItemLayoutSaveModel,
	HubItemMediaSaveModel,
	HubItemRowSaveModel,
	ImageElementSaveModel,
	ImageHubMediaSaveModel,
	HubHierarchyListCategoryNodeModel,
	HubHierarchyListNodeModel
} from "client/Api/Hub/common.composer";
import {
	ConfirmationDialogInitialData,
	FormattedText,
	SelectItem
} from "client/Components";
import { ObjectWithClientId } from "client/Misc/clientId";
import { Base64FileUriUploadWithMetadataAndAttributionModel } from "client/Misc/commonTypes";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { newGuid } from "client_legacy/ndaw/guid";

export type ImageHubMediaFormModel = OverrideProperties<
	ImageHubMediaSaveModel,
	{ image: OptionalModel<Base64FileUriUploadWithMetadataAndAttributionModel>; }
>;

export type HubItemMediaFormModel =
	| Exclude<HubItemMediaSaveModel, ImageHubMediaSaveModel>
	| ImageHubMediaFormModel;

export type ImageElementFormModel = OverrideProperties<
	ImageElementSaveModel,
	{
		image: OptionalModel<Base64FileUriUploadWithMetadataAndAttributionModel>;
	}
>;

export type FrequentlyAskedQuestionElementFormModel = OverrideProperties<
	FrequentlyAskedQuestionElementSaveModel,
	{
		items: readonly ObjectWithClientId<FrequentlyAskedQuestionItemSaveModel>[];
	}
>;

export type HubItemElementFormModel =
	| Exclude<
		HubItemElementSaveModel,
		| ImageElementSaveModel
		| FrequentlyAskedQuestionElementSaveModel
	>
	| ImageElementFormModel
	| FrequentlyAskedQuestionElementFormModel;

export type HubItemLayoutFormModel = OverrideProperties<
	HubItemLayoutSaveModel,
	{
		rows: readonly ObjectWithClientId<HubItemRowFormModel>[];
	}
>;

export type HubItemRowFormModel = OverrideProperties<
	HubItemRowSaveModel,
	{
		columns: readonly ObjectWithClientId<HubItemColumnFormModel>[];
	}
>;

export type HubItemColumnFormModel = OverrideProperties<
	HubItemColumnSaveModel,
	{
		elements: readonly ObjectWithClientId<HubItemElementFormModel>[];
	}
>;

export function getDisableTheShowAutomaticallySettingDialogProps(
	isDraft: boolean
): ConfirmationDialogInitialData {
	const trackingId: string = "save-without-direct-popup-confirmation-dialog";
	return isDraft
		? {
			title: localize("_DirectPopupSettingsWillBeTurnedOff"),
			children: (
				<FormattedText
					text={localize("_CannotSendDirectPopupsDraftDescription")}
					thingsToReplace={[
						{
							what: "{{PopUpSettingName}}",
							filler: localize("_ShowPopupDirectlyToUser")
						}
					]}
				/>
			),
			confirmText: localize("_SaveAsDraft"),
			trackingId
		}
		: {
			title: localize("_CannotSendADirectPopUp"),
			children: (
				<FormattedText
					text={localize("_CannotSendDirectPopupsPublishDescription")}
					thingsToReplace={[
						{
							what: "{{PopUpSettingName}}",
							filler: localize("_ShowPopupDirectlyToUser")
						}
					]}
				/>
			),
			confirmText: localize("_Publish"),
			trackingId
		};
}

export function findHubCategoryItemNodes(
	categoryHierarchy: readonly HubHierarchyListNodeModel[],
	categoryIds: readonly number[],
): readonly HubHierarchyListCategoryNodeModel[] {
	const result: HubHierarchyListCategoryNodeModel[] = [];

	for (const node of categoryHierarchy) {
		switch (node.kind) {
			case HubItemCategoryNodeKind.Category:
				if (categoryIds.includes(node.category.id)) {
					result.push(node);
				}
				break;
			case HubItemCategoryNodeKind.Group:
				result.push(
					...findHubCategoryItemNodes(node.children, categoryIds)
				);

				break;
			default:
				never(node);
		}
	}

	return result;
}

export function tryFindHubCategoryItemNode(
	categoryHierarchy: readonly HubHierarchyListNodeModel[],
	categoryId: number
): TryResult<HubHierarchyListCategoryNodeModel> {
	for (const node of categoryHierarchy) {
		switch (node.kind) {
			case HubItemCategoryNodeKind.Category:
				if (node.category.id === categoryId) {
					return {
						wasSuccessful: true,
						result: node
					};
				}
				break;
			case HubItemCategoryNodeKind.Group:
				const result: TryResult<HubHierarchyListCategoryNodeModel> = tryFindHubCategoryItemNode(node.children, categoryId);

				if (result.wasSuccessful) {
					return result;
				}
				break;
			default:
				never(node);
		}
	}

	return { wasSuccessful: false };
}

export function convertHubHierarchyListNodeToSelectItem(
	nodes: readonly HubHierarchyListNodeModel[],
): readonly SelectItem<number>[] {
	const result: SelectItem<number>[] = [];

	for (const node of nodes) {
		switch (node.kind) {
			case HubItemCategoryNodeKind.Category:
				const { category } = node;

				if (!category.canCreateItem) {
					continue;
				}

				result.push({
					value: category.id,
					text: category.name
				});
				break;
			case HubItemCategoryNodeKind.Group:
				const { name } = node;
				const children: readonly SelectItem<number>[] = convertHubHierarchyListNodeToSelectItem(node.children);

				if (children.length > 0) {
					result.push({
						uniqueId: newGuid(),
						text: name,
						children
					});
				}
				break;
			default:
				never(node);
		}
	}

	return result;
}